import { Check, ChevronDown, HelpCircle, RefreshCw } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { timeAgo } from "./format";

/** Ячейка грида аналитики: числовая (right, tabular) по умолчанию; text — левое выравнивание. */
export function Cell({
  children,
  text,
  className,
  title,
}: {
  children?: React.ReactNode;
  text?: boolean;
  className?: string;
  title?: string;
}): React.ReactElement {
  return (
    <div
      title={title}
      className={cn("truncate px-2 py-0.5", text ? "text-left" : "text-right tabular-nums", className)}
    >
      {children}
    </div>
  );
}

/** Мультиселект (Tailwind, с галочками). Плоский (`all`) ИЛИ с секциями (`groups` — напр. регионы
 *  по каналам). `labelOf` — отображаемое имя пункта (напр. код канала → «Wildberries»). */
export function RegionMultiSelect({
  all,
  groups,
  selected,
  onChange,
  allLabel = "Все регионы",
  countWord = "Регионов",
  labelOf,
}: {
  all?: string[];
  groups?: { label: string; items: string[] }[];
  selected: Set<string>;
  onChange: (s: Set<string>) => void;
  allLabel?: string;
  countWord?: string;
  labelOf?: (v: string) => string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as HTMLElement)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = (r: string): void => {
    const next = new Set(selected);
    if (next.has(r)) next.delete(r);
    else next.add(r);
    onChange(next);
  };
  const disp = (r: string): string => (labelOf ? labelOf(r) : r);
  const label =
    selected.size === 0
      ? allLabel
      : selected.size === 1
        ? disp([...selected][0] ?? "")
        : `${countWord}: ${selected.size}`;

  const renderItem = (r: string): React.ReactElement => {
    const on = selected.has(r);
    return (
      <button
        key={r}
        type="button"
        onClick={() => toggle(r)}
        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
      >
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded border",
            on ? "border-primary bg-primary text-primary-foreground" : "border-input",
          )}
        >
          {on && <Check className="size-3" />}
        </span>
        <span className="truncate">{disp(r)}</span>
      </button>
    );
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex h-10 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm hover:bg-accent"
      >
        <span className="max-w-[160px] truncate">{label}</span>
        <ChevronDown className="size-4 shrink-0 opacity-60" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 max-h-72 w-64 overflow-auto rounded-md border bg-popover p-1 shadow-md">
          <button
            type="button"
            onClick={() => onChange(new Set())}
            className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent"
          >
            {allLabel}
            {selected.size === 0 && <Check className="size-4 text-primary" />}
          </button>
          <div className="my-1 h-px bg-border" />
          {groups
            ? groups.map((g) => (
                <div key={g.label}>
                  <div className="mt-1 px-2 py-1 text-xs font-semibold uppercase text-muted-foreground">
                    {g.label}
                  </div>
                  {g.items.map(renderItem)}
                </div>
              ))
            : (all ?? []).map(renderItem)}
        </div>
      )}
    </div>
  );
}

/** Значок «?» с подсказкой по наведению. Своя, а не системный `title`: системная всплывает через
 *  секунду, обрезает длинный текст в одну строку и не даёт выделить в нём формулу — а объяснять
 *  тут надо именно смысл настройки, а не повторять её название. Значок нужен затем, чтобы было
 *  видно, что наводить вообще есть на что. */
export function Hint({
  children,
  side = "bottom",
}: {
  children: React.ReactNode;
  side?: "bottom" | "left";
}): React.ReactElement {
  return (
    <span className="group relative inline-flex shrink-0 cursor-help items-center">
      <HelpCircle className="size-3 text-muted-foreground/60 transition-colors group-hover:text-foreground" />
      <span
        className={cn(
          "pointer-events-none invisible absolute z-50 w-72 rounded-md border bg-popover px-3 py-2",
          "text-left text-xs font-normal leading-relaxed text-popover-foreground shadow-lg",
          "group-hover:visible",
          side === "bottom" ? "left-1/2 top-full mt-1.5 -translate-x-1/2" : "right-full top-1/2 mr-1.5 -translate-y-1/2",
        )}
      >
        {children}
      </span>
    </span>
  );
}

/** Полоса свежести снимка + refresh — общая для вкладок (принцип 4). */
export function SnapshotBar({
  snapshotAt,
  onRefresh,
  refreshing,
}: {
  snapshotAt: string | null;
  onRefresh: () => void;
  refreshing: boolean;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card px-4 py-3">
      <div className="text-sm">
        <span className="text-muted-foreground">Снимок остатков: </span>
        {snapshotAt ? (
          <span className="font-medium">
            {timeAgo(snapshotAt)}{" "}
            <span className="text-muted-foreground">
              ({new Date(snapshotAt).toLocaleString("ru-RU")})
            </span>
          </span>
        ) : (
          <span className="font-medium text-muted-foreground">ещё не создан</span>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={onRefresh} disabled={refreshing}>
        <RefreshCw className={cn("size-4", refreshing && "animate-spin")} />
        Обновить снимок
      </Button>
    </div>
  );
}

/** Почему «Доупак» недоступен. Один словарь на обе вкладки — раньше подписи и тексты
 *  дублировались четырьмя копиями и уже успели разойтись между «Распределением» и «Производством». */
export const ASSEMBLY_BLOCK: Record<
  string,
  { badge: string; cell: string; badgeCls: string; cellCls: string; title: string }
> = {
  no_base: {
    badge: "без флакона",
    cell: "н/п",
    badgeCls: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    cellCls: "text-muted-foreground",
    title:
      "Товар вне флаконной модели: спецификация не разворачивается в базовый флакон, доупаковать его нельзя. Доступно только «Готово».",
  },
  spec_problem: {
    badge: "спека сломана",
    cell: "ошибка",
    badgeCls: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    cellCls: "text-red-600 dark:text-red-400",
    title:
      "Спецификацию не удалось развернуть (нет спеки / цикл / компонент без категории) — это дефект данных 1С, а не «нет потребности». Доупак посчитать нельзя.",
  },
};

ASSEMBLY_BLOCK.target_off = {
  badge: "выключен",
  cell: "—",
  badgeCls: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  cellCls: "text-muted-foreground",
  title: "Выключено в ВАШЕМ «Куда планируем»: вы сюда не планируете и не фиксируете (у других пользователей — своя настройка). Потребность показана — видно, чего стоит запрет.",
};

/** Бейдж состояния сборки товара; ничего не рисует, когда блокировки нет. */
export function AssemblyBlockBadge({ block }: { block?: string }): React.ReactElement | null {
  const b = block ? ASSEMBLY_BLOCK[block] : undefined;
  if (!b) return null;
  return (
    <span
      className={cn("shrink-0 rounded px-1 text-[10px] font-medium", b.badgeCls)}
      title={b.title}
    >
      {b.badge}
    </span>
  );
}
